import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import PrincipalEvaluationPrint from "@/components/PrincipalEvaluationPrint";
import { EVALUATION_MONTHS, EVALUATION_PERIODS, evaluationPeriodText } from "@/lib/evaluationPeriod";
import { staffTitles } from "@/lib/staffTitles";

const emptyPeriod = () => ({ kind: "", month: "", year: "", academic_year: "", semester: "", start_date: "", end_date: "" });
function periodPayload(draft) {
  if (draft.kind === "monthly") return { kind: draft.kind, month: `${draft.year}-${draft.month}` };
  if (draft.kind === "semester") return { kind: draft.kind, academic_year: Number(draft.academic_year), semester: draft.semester };
  if (draft.kind === "annual") return { kind: draft.kind, academic_year: Number(draft.academic_year) };
  return { kind: draft.kind, start_date: draft.start_date, end_date: draft.end_date };
}

export default function PrincipalEvaluation({ counselorId }) {
  const { user } = useAuth();
  const allowed = user?.role === "principal" || user?.role === "counselor" || (user?.role === "admin" && user?.can_evaluate_counselors);
  const [entries, setEntries] = useState([]);
  const [options, setOptions] = useState(null);
  const [ratings, setRatings] = useState({});
  const [notes, setNotes] = useState("");
  const [period, setPeriod] = useState(emptyPeriod);
  const [editingId, setEditingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [printEntry, setPrintEntry] = useState(null);
  const finishPrint = useCallback(() => setPrintEntry(null), []);
  const [requestId, setRequestId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const target = counselorId || user?.id;
  useEffect(() => {
    if (!allowed || !target) return;
    let active = true;
    setLoading(true);
    setError("");
    setEntries([]);
    setRatings({});
    setNotes("");
    setPeriod(emptyPeriod());
    setEditingId(null);
    setConfirmDelete(null);
    setPrintEntry(null);
    setRequestId(null);
    Promise.all([
      api.get("/principal-evaluations/options"),
      api.get("/principal-evaluations", { params: { counselor_id: target } }),
    ]).then(([schema, results]) => {
      if (active) { setOptions(schema.data); setEntries(results.data); }
    }).catch((failure) => {
      if (active) setError(formatErr(failure));
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [allowed, target]);
  if (!allowed) return null;
  const save = async (event) => {
    event.preventDefault();
    if (saving || deletingId) return;
    if (!options) return;
    if (Object.keys(options.criteria).some((key) => !ratings[key])) {
      setError("اختر تقييماً لكل معيار؛ يمكنك اختيار «لم تتم ملاحظته».");
      return;
    }
    if (!period.kind || (period.kind === "monthly" && (!period.month || !/^[0-9]{4}$/.test(period.year) || Number(period.year) < 1))
      || (["semester", "annual"].includes(period.kind) && (!/^[0-9]{4}$/.test(period.academic_year) || Number(period.academic_year) < 1300 || Number(period.academic_year) > 1600))
      || (period.kind === "semester" && !period.semester)
      || (period.kind === "range" && (!period.start_date || !period.end_date))) {
      setError("حدد نوع فترة التقييم وأكمل الشهر والسنة أو العام الدراسي أو تاريخي البداية والنهاية.");
      return;
    }
    if (period.kind === "range" && period.end_date < period.start_date) {
      setError("تاريخ النهاية يجب ألا يسبق تاريخ البداية.");
      return;
    }
    setSaving(true);
    setError("");
    const payload = { ratings, notes, period: periodPayload(period) };
    const fingerprint = JSON.stringify(payload);
    const identity = requestId?.fingerprint === fingerprint ? requestId.id : crypto.randomUUID();
    setRequestId({ id: identity, fingerprint });
    try {
      const { data } = editingId
        ? await api.put(`/principal-evaluations/${encodeURIComponent(editingId)}`, payload)
        : await api.post("/principal-evaluations", { request_id: identity, counselor_id: target, ...payload });
      setEntries((current) => [data, ...current.filter((item) => item.id !== data.id)]);
      setRatings({});
      setNotes("");
      setPeriod(emptyPeriod());
      setEditingId(null);
      setRequestId(null);
      toast.success("تم حفظ التقييم؛ يظهر للمرشد المعني والمشرف الإرشادي المخوّل");
    } catch (failure) {
      setError(formatErr(failure));
      toast.error(formatErr(failure));
    } finally { setSaving(false); }
  };
  const edit = (entry) => {
    setEditingId(entry.id);
    setRatings({ ...entry.ratings });
    setNotes(entry.notes || "");
    const saved = entry.period;
    const [year = "", month = ""] = saved?.month?.split("-") || [];
    setPeriod({ ...emptyPeriod(), ...saved, academic_year: saved?.academic_year ? String(saved.academic_year) : "", year, month });
    setRequestId(null);
    setError("");
  };
  const remove = async () => {
    if (!confirmDelete || saving || deletingId) return;
    const id = confirmDelete.id;
    setDeletingId(id);
    setError("");
    try {
      await api.delete(`/principal-evaluations/${encodeURIComponent(id)}`);
      setEntries((current) => current.filter((entry) => entry.id !== id));
      setConfirmDelete(null);
      if (editingId === id) { setEditingId(null); setRatings({}); setNotes(""); setPeriod(emptyPeriod()); setRequestId(null); }
      toast.success("تم حذف التقييم");
    } catch (failure) { setError(formatErr(failure)); toast.error(formatErr(failure)); }
    finally { setDeletingId(null); }
  };
  const busy = saving || Boolean(deletingId);
  const fieldClass = "block w-full rounded-lg border border-[#e6e2d8] p-2";
  const setPeriodField = (field, value) => setPeriod((current) => ({ ...current, [field]: value }));
  return (
    <section className="no-print space-y-4 rounded-2xl border border-[#e6e2d8] bg-white p-5" data-testid="principal-evaluation">
      <h2 className="text-lg font-bold">تقييم {staffTitles(user?.role === "principal" ? user.gender : "").principal} للأداء الإرشادي</h2>
      <p className="text-sm text-[#4A554E]">يظهر للمرشد صاحب التقييم والمشرف الإرشادي المخوّل، ولا يمثل اعتماداً للسجلات.</p>
      {loading ? <p>جاري تحميل التقييمات...</p> : options && (
        <>
          {user?.role === "principal" && <form onSubmit={save} className="space-y-3">
            <h3 className="font-semibold">{editingId ? "تعديل التقييم" : "إضافة تقييم"}</h3>
            <label className="block space-y-1 text-sm">نوع فترة التقييم
              <select aria-label="نوع فترة التقييم" className={fieldClass} disabled={busy} value={period.kind} onChange={(event) => setPeriodField("kind", event.target.value)}>
                <option value="">اختر الفترة</option>
                {Object.entries(EVALUATION_PERIODS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </label>
            {period.kind === "monthly" && <div className="grid gap-3 sm:grid-cols-2">
              <label className="text-sm">الشهر الميلادي<select aria-label="الشهر الميلادي" className={fieldClass} disabled={busy} value={period.month} onChange={(event) => setPeriodField("month", event.target.value)}>
                <option value="">اختر الشهر</option>{EVALUATION_MONTHS.map((name, index) => <option key={name} value={String(index + 1).padStart(2, "0")}>{name}</option>)}
              </select></label>
              <label className="text-sm">السنة الميلادية<input aria-label="السنة الميلادية" type="number" min="1" max="9999" className={fieldClass} disabled={busy} value={period.year} onChange={(event) => setPeriodField("year", event.target.value)} placeholder="2026" /></label>
            </div>}
            {["semester", "annual"].includes(period.kind) && <label className="block text-sm">العام الدراسي (هجري)
              <input aria-label="العام الدراسي (هجري)" type="number" min="1300" max="1600" className={fieldClass} disabled={busy} value={period.academic_year} onChange={(event) => setPeriodField("academic_year", event.target.value)} placeholder="1448" />
            </label>}
            {period.kind === "semester" && <label className="block text-sm">الفصل الدراسي
              <select aria-label="الفصل الدراسي" className={fieldClass} disabled={busy} value={period.semester} onChange={(event) => setPeriodField("semester", event.target.value)}>
                <option value="">اختر الفصل</option><option value="first">الأول</option><option value="second">الثاني</option>
              </select>
            </label>}
            {period.kind === "range" && <div className="grid gap-3 sm:grid-cols-2">
              <label className="text-sm">من تاريخ (ميلادي)<input aria-label="من تاريخ (ميلادي)" type="date" className={fieldClass} disabled={busy} value={period.start_date} onChange={(event) => setPeriodField("start_date", event.target.value)} /></label>
              <label className="text-sm">إلى تاريخ (ميلادي)<input aria-label="إلى تاريخ (ميلادي)" type="date" className={fieldClass} disabled={busy} min={period.start_date || undefined} value={period.end_date} onChange={(event) => setPeriodField("end_date", event.target.value)} /></label>
            </div>}
            {Object.entries(options.criteria).map(([key, label]) => (
              <label key={key} className="block space-y-1 text-sm">{label}
                <select aria-label={label} value={ratings[key] || ""} disabled={busy} onChange={(event) => { setRatings((current) => ({ ...current, [key]: event.target.value })); }} className="block w-full rounded-lg border border-[#e6e2d8] p-2">
                  <option value="">اختر التقييم</option>
                  {Object.entries(options.choices).map(([value, text]) => <option key={value} value={value}>{text}</option>)}
                </select>
              </label>
            ))}
            <label className="block text-sm">ملاحظات {staffTitles(user?.gender).principal}
              <Textarea aria-label="ملاحظات التقييم" value={notes} maxLength={5000} disabled={busy} onChange={(event) => setNotes(event.target.value)} />
            </label>
            <Button type="submit" disabled={busy}>{saving ? "جاري الحفظ..." : editingId ? "حفظ التعديلات" : "حفظ التقييم"}</Button>
            {editingId && <Button type="button" variant="outline" disabled={busy} onClick={() => { setEditingId(null); setRatings({}); setNotes(""); setPeriod(emptyPeriod()); setRequestId(null); setError(""); }}>إلغاء التعديل</Button>}
          </form>}
          {entries.length ? entries.map((entry) => (
            <article key={entry.id} className="space-y-2 rounded-xl border border-[#e6e2d8] p-3">
              <h3 className="font-semibold">{staffTitles(entry.principal_gender || entry.gender).principal}: {entry.principal_name} — {new Date(entry.created_at).toLocaleString("ar-SA")}</h3>
              <p className="text-sm">{staffTitles(entry.gender).counselor}: {entry.counselor_name}</p>
              <p className="font-semibold text-sm">{evaluationPeriodText(entry.period)}</p>
              {entry.updated_at && <p className="text-xs">آخر تعديل: {new Date(entry.updated_at).toLocaleString("ar-SA")}</p>}
              {Object.entries(entry.ratings).map(([key, value]) => <p key={key} className="text-sm">{options.criteria[key]}: {options.choices[value]}</p>)}
              {entry.notes && <p className="whitespace-pre-wrap text-sm">{entry.notes}</p>}
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" disabled={busy || Boolean(printEntry)} onClick={() => setPrintEntry(entry)}>طباعة التقييم</Button>
                {user?.role === "principal" && entry.principal_id === user.id && <>
                  <Button type="button" variant="outline" disabled={busy} onClick={() => edit(entry)}>تعديل التقييم</Button>
                  <Button type="button" variant="outline" disabled={busy} onClick={() => setConfirmDelete(entry)}>حذف التقييم</Button>
                </>}
              </div>
            </article>
          )) : <p className="text-sm text-[#4A554E]">لا توجد تقييمات محفوظة بعد.</p>}
        </>
      )}
      {error && <p role="alert" className="text-red-700">{error}</p>}
      {confirmDelete && <div role="alertdialog" aria-label="تأكيد حذف التقييم" className="space-y-2 rounded-xl border border-red-300 p-3">
        <p>هل تريد حذف التقييم: {evaluationPeriodText(confirmDelete.period)}؟ لا يمكن التراجع عن الحذف.</p>
        <Button type="button" disabled={busy} onClick={remove}>{deletingId ? "جاري الحذف..." : "تأكيد الحذف"}</Button>
        <Button type="button" variant="outline" disabled={busy} onClick={() => setConfirmDelete(null)}>إلغاء الحذف</Button>
      </div>}
      <PrincipalEvaluationPrint entry={printEntry} options={options} onFinish={finishPrint} />
    </section>
  );
}
